import React from 'react';
import { 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Box, 
  Typography, 
  Chip, 
  CircularProgress, 
  Grow 
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { ProcessingStep } from '../types';

interface ProcessingStepItemProps {
  step: ProcessingStep;
  index: number;
}

export const ProcessingStepItem: React.FC<ProcessingStepItemProps> = ({ step, index }) => {
  const getStepIcon = (step: ProcessingStep) => {
    if (step.status === 'completed') {
      return <CheckCircle sx={{ color: '#10b981', fontSize: '1.25rem' }} />;
    } else if (step.status === 'processing') {
      return <CircularProgress size={18} sx={{ color: 'hsl(var(--accent))' }} />;
    } else {
      return <RadioButtonUnchecked sx={{ color: '#d1d5db', fontSize: '1.25rem' }} />;
    }
  };

  return (
    <Grow
      in={true}
      timeout={300 + index * 100}
      style={{ transformOrigin: '0 0 0' }}
    >
      <ListItem
        sx={{
          py: 1.25,
          px: 1.25,
          borderRadius: 2,
          mb: 1,
          bgcolor: '#f8fafc',
          border: '1px solid #e2e8f0',
          boxShadow: step.status === 'processing'
            ? '0 0 0 1px rgba(16, 185, 129, 0.18), 0 6px 16px rgba(15, 23, 42, 0.06)'
            : '0 2px 6px rgba(15, 23, 42, 0.04)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)'
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          {getStepIcon(step)}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#1f2937',
                  fontWeight: step.status === 'processing' ? 600 : 500,
                  fontSize: '0.875rem',
                  flex: 1
                }}
              >
                {step.label}
              </Typography>
              {step.status === 'completed' && (
                <Chip
                  label="Done"
                  size="small"
                  sx={{
                    backgroundColor: '#dcfce7',
                    color: '#166534',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 20,
                    border: 'none !important',
                    borderWidth: 0,
                    borderStyle: 'none'
                  }}
                />
              )}
              {step.status === 'processing' && (
                <Chip
                  label="Processing"
                  size="small"
                  sx={{
                    backgroundColor: '#cffafe',
                    color: '#155e75',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 20,
                    border: 'none !important',
                    borderWidth: 0,
                    borderStyle: 'none'
                  }}
                />
              )}
            </Box>
          }
          secondary={
            step.timestamp && step.status === 'completed' ? (
              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                  fontSize: '0.75rem',
                  mt: 0.5,
                  display: 'block'
                }}
              >
                Completed at {step.timestamp}
              </Typography>
            ) : null
          }
        />
      </ListItem>
    </Grow>
  );
}; 